import { useState, useMemo } from 'react';
import { ListFilter, Hospital } from 'lucide-react';
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Checkbox,
  Separator,
} from '@predoc/cow';
import { useListProfiles } from '../../../profiles/hooks/profilesAPI';
import { getProfileColor } from '@/utils';
import { useDebounce } from '@/hooks';

interface ProfileFilterDropdownProps {
  selectedProfileIds: string[];
  onProfileIdsChange: (profileIds: string[]) => void;
}

export const ProfileFilterDropdown = ({
  selectedProfileIds,
  onProfileIdsChange,
}: ProfileFilterDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);

  const { data: profilesData, isLoading } = useListProfiles({
    assignedProfilesOnly: true,
    searchTerms: debouncedSearch ? [debouncedSearch] : undefined,
    pagination: {
      offset: 0,
      limit: 100,
    },
  });

  const { data: totalData } = useListProfiles({
    assignedProfilesOnly: true,
    pagination: { offset: 0, limit: 1 },
  });
  const totalProfileCount = totalData?.totalCount ?? 0;

  const profiles = useMemo(
    () => profilesData?.data || [],
    [profilesData?.data],
  );

  const handleOpenChange = (next: boolean) => {
    if (!next) setSearchTerm('');
    setOpen(next);
  };

  const handleProfileChange = (profileId: string, checked: boolean) => {
    const newProfileIds = checked
      ? [...selectedProfileIds, profileId]
      : selectedProfileIds.filter((id) => id !== profileId);

    onProfileIdsChange(newProfileIds);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const newIds = Array.from(
        new Set([...selectedProfileIds, ...profiles.map((p) => p.id)]),
      );
      onProfileIdsChange(newIds);
    } else {
      onProfileIdsChange([]);
    }
  };

  // Show red dot when NOT all profiles are selected
  const hasActiveFilters =
    totalProfileCount > 0 &&
    selectedProfileIds.length > 0 &&
    selectedProfileIds.length !== totalProfileCount;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground relative h-6 w-6 p-0"
          aria-label="Filter profiles"
        >
          <ListFilter className="size-3" />
          {hasActiveFilters && (
            <span className="bg-destructive absolute top-0 right-0 h-2 w-2 rounded-full" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="flex w-80 flex-col p-0" align="end">
        <div className="p-2 pb-0">
          <Input
            placeholder="Search profiles..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchTerm(e.target.value)
            }
            className="h-8 text-sm"
          />
        </div>
        {!debouncedSearch && totalProfileCount > 100 && (
          <p className="text-muted-foreground px-2 pt-1 text-xs">
            Showing first 100 of {totalProfileCount}. Search to find more.
          </p>
        )}
        <div className="max-h-[400px] overflow-y-auto">
          <div className="p-2">
            {profiles.map((profile) => {
              const isSelected = selectedProfileIds.includes(profile.id);
              const profileColor = getProfileColor(profile.avatarType);

              return (
                <div key={profile.id} className="flex items-center py-1">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(checked: boolean) =>
                      handleProfileChange(profile.id, checked as boolean)
                    }
                    label={
                      <div className="inline-flex items-center gap-2 whitespace-nowrap">
                        <Hospital
                          className="size-4"
                          style={{ color: profileColor }}
                          aria-hidden="true"
                        />
                        <span className="text-sm">{profile.displayName}</span>
                      </div>
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>
        {profiles.length > 0 && (
          <>
            <Separator />
            <div className="flex flex-row justify-start gap-2 p-2">
              <Button
                variant="link"
                className="h-auto p-0"
                onClick={() => handleSelectAll(true)}
                disabled={isLoading}
              >
                Select All
              </Button>
              <Button
                variant="link"
                className="h-auto p-0"
                onClick={() => handleSelectAll(false)}
                disabled={isLoading}
              >
                Select None
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
